import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ExternalSyncError } from '@common/entities';
import { useMutation } from '@tanstack/react-query';
import { NotificationStatus } from 'enums/notification-status';
import {
  resetSaveState,
  resetSaveTrigger,
  setLoadingState,
  setSaveCompleted,
} from 'state/slices/actionsSlice';
import { updateApplicantResponses } from 'state/slices/leadProfileSlice';
import {
  setExternalSyncErrors,
  updateLeasingJourneyErrors,
  updateStepStatus,
} from 'state/slices/navigationSlice';
import { updateResponseState } from 'state/slices/responseSlice';
import { RootState } from 'state/store';

import notification from 'components/notification';
import { patchResponseForId } from 'services';
import { areObjectsEqual, deepCopy } from 'utils';
import {
  excludeHiddenGuarantorErrors,
  excludeHiddenGuarantorResponse,
  isCountBasedGuarantorHidden,
} from 'utils/display-logic/hiddenGuarantorResponses';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { prepareApplicantResponseData } from 'utils/form';
import { getErrorCount } from 'utils/getErrors';
import { getDisplayableExternalSyncErrors } from 'utils/getExternalSyncErrors';
import handleError from 'utils/handleError';

import { useEvaluateDisplayCondition } from './display-logic/useEvaluateDisplayCondition';
import { useGetPageData } from './useGetPageData';
import {
  getSecondaryApplicantSaveErrors,
  useSyncSecondaryApplicantsForPageSave,
} from './useSyncSecondaryApplicantsForPageSave';

interface Props {
  activeStepIndex: number;
  responseId?: string;
}

export const useSaveBlankPageData = (props: Props) => {
  const { activeStepIndex, responseId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const loadingText = '';

  const {
    getValues,
    getFieldState,
    formState: { errors },
  } = useFormContext();

  const dispatch = useDispatch();
  const { form } = useSelector((state: RootState) => state.application);
  const evaluateDisplayCondition = useEvaluateDisplayCondition();
  const { isManualSave } = useSelector((state: RootState) => state.actions);
  const { currentTabIndex, leadProfileResponseId } = useSelector(
    (state: RootState) => state.navigation,
  );
  const { applicant, activeResponse } = useSelector((state: RootState) => state.application);
  const { isLeadProfile } = useSelector(
    (state: RootState) => state.auth ?? { isLeadProfile: false },
  );
  const { applicationId: LPApplicationId } = useSelector(
    (state: RootState) => state.leadProfile.primaryApplicant.currentResponse,
  );

  const effectiveResponseId = isLeadProfile ? leadProfileResponseId : activeResponse.responseId;

  const { propertyId } = applicant;
  const { applicationId } = activeResponse;

  // Baseline the dirty check against the CURRENT tab's saved data. activeResponse.responseId is
  // always the primary (setActiveResponse is never dispatched), so using it here made every
  // non-primary tab look dirty and triggered a spurious save on tab switch.
  const activeTabApplicant = useSelector((state: RootState) =>
    state.leadProfile.applicants.find((a) => a.responseId === effectiveResponseId),
  );
  const responseDetails = activeTabApplicant?.responseDetails;
  const activeTabApplicantEmail = activeTabApplicant?.email;

  const { pages } = form;

  const [initialFormValues, setInitialFormValues] = useState({});

  useEffect(() => {
    if (responseDetails) {
      setInitialFormValues(prepareApplicantResponseData(responseDetails));
    }
  }, [responseDetails]);

  const syncSecondaryApplicantsForPageSave = useSyncSecondaryApplicantsForPageSave();

  const { mutateAsync: updateResponseForId } = useMutation({
    mutationFn: patchResponseForId,
  });

  const { mutateAsync: updateResponseLeadProfile } = useMutation({
    mutationFn: patchResponseForId,
    onError: (error) => {
      dispatch(resetSaveState());
      const message = extractErrorMessage(error);
      handleError(`Form Viewer - Unable to save response ${error}`, `${message}`);
    },
  });

  const prepareData = useGetPageData();

  function isPropertyIdAvailable() {
    let isAvailable = true;
    if (!propertyId) {
      isAvailable = false;
      handleError(`Form Viewer - Property ID not available`, `Property ID not available`);
    }
    return isAvailable;
  }

  async function saveBlankPage(targetIndex: number, validationErrorCount?: number) {
    if (!isPropertyIdAvailable()) return false;
    setIsLoading(true);

    try {
      const isGuarantorHidden = isCountBasedGuarantorHidden({
        form,
        pageId: pages[activeStepIndex].id,
        evaluateDisplayCondition,
      });
      const preparedData = excludeHiddenGuarantorResponse(
        prepareData(activeStepIndex),
        isGuarantorHidden,
      );
      const pageResponses = await syncSecondaryApplicantsForPageSave(
        preparedData,
        pages[activeStepIndex].id,
        // RenderPage unmounts its fields while the global loading screen is shown.
        () => dispatch(setLoadingState({ isLoading: true })),
      );
      const errorCount = Math.max(
        validationErrorCount ?? 0,
        getErrorCount(
          excludeHiddenGuarantorErrors(
            getSecondaryApplicantSaveErrors(errors, getFieldState),
            isGuarantorHidden,
          ),
        ),
      );

      const requestData: any = {
        currentPageId: pages[activeStepIndex].id,
        targetPageId: responseId ? pages[activeStepIndex].id : pages[targetIndex].id,
        applicationId: isLeadProfile ? LPApplicationId : applicationId,
        responses: [...pageResponses],
        propertyId,
        errorCount,
        ...(isLeadProfile && activeTabApplicantEmail ? { emailId: activeTabApplicantEmail } : {}),
      };

      // After the async save, effectiveResponseId may have advanced to the next tab, so we
      // must capture both the responseId and the form values here before any await.
      const savedFormValues = deepCopy(getValues());
      const isFormDirty = !areObjectsEqual(savedFormValues, initialFormValues);

      let syncErrorCount = errorCount;
      if (isFormDirty && isLeadProfile) {
        const savedResponseId = effectiveResponseId as string;
        const { data: updateResult } = await updateResponseLeadProfile({
          responseId: savedResponseId,
          responseData: requestData,
        });
        const externalSync = updateResult?.externalSync;
        const externalSyncErrors = getDisplayableExternalSyncErrors(externalSync, errorCount);

        dispatch(
          setExternalSyncErrors({
            tabIndex: currentTabIndex,
            pageId: requestData.currentPageId,
            errors: externalSyncErrors,
          }),
        );

        if (externalSyncErrors.length > 0) {
          syncErrorCount = Math.max(errorCount, 1);
          dispatch(
            updateLeasingJourneyErrors({
              pageIndex: activeStepIndex,
              count: syncErrorCount,
            }),
          );
          dispatch(
            updateStepStatus({
              tabIndex: currentTabIndex,
              step: requestData.currentPageId,
              status: { errorCount: syncErrorCount },
            }),
          );

          handleError(
            'Lead Profile external sync failed',
            externalSyncErrors.map((error: ExternalSyncError) => error.message).join('; ') ??
              'Unknown error syncing data',
          );
        }

        dispatch(setSaveCompleted(true));
        dispatch(updateApplicantResponses({ responseId: savedResponseId, data: savedFormValues }));
        if (isManualSave && !externalSync?.errors?.length) {
          notification('Save successful', NotificationStatus.SUCCESS);
        }
        dispatch(resetSaveTrigger());
      } else if (!responseId) {
        const { data: updateResult } = await updateResponseForId({
          responseId: effectiveResponseId as string,
          responseData: requestData,
        });
        const responseNameUpdates: Record<string, unknown> = {};
        if (savedFormValues.applicant_name_first !== undefined) {
          responseNameUpdates.applicant_name_first = savedFormValues.applicant_name_first;
        }
        if (savedFormValues.applicant_name_last !== undefined) {
          responseNameUpdates.applicant_name_last = savedFormValues.applicant_name_last;
        }
        if (Object.keys(responseNameUpdates).length > 0) {
          dispatch(updateResponseState(responseNameUpdates));
        }
        const externalSync = updateResult?.externalSync;
        const externalSyncErrors = getDisplayableExternalSyncErrors(externalSync, errorCount);
        dispatch(
          setExternalSyncErrors({
            tabIndex: 0,
            pageId: requestData.currentPageId,
            errors: externalSyncErrors,
          }),
        );
        if (externalSyncErrors.length > 0) {
          syncErrorCount = Math.max(errorCount, 1);
          dispatch(
            updateLeasingJourneyErrors({
              pageIndex: activeStepIndex,
              count: syncErrorCount,
            }),
          );
        }
        dispatch(setSaveCompleted(true));
        dispatch(resetSaveTrigger());
      } else {
        dispatch(setSaveCompleted(true));
        dispatch(resetSaveTrigger());
      }

      dispatch(
        updateLeasingJourneyErrors({
          pageIndex: activeStepIndex,
          count: syncErrorCount,
        }),
      );
      return true;
    } catch (error: any) {
      if (isLeadProfile) {
        dispatch(resetSaveState());
      }
      const msg = extractErrorMessage(error);
      handleError(`Form Viewer - ${error}`, `${msg}`);
      return false;
    } finally {
      dispatch(setLoadingState({ isLoading: false }));
      setIsLoading(false);
    }
  }

  return {
    isLoading,
    loadingText,
    saveBlankPage,
  };
};
